import React from 'react';
import { Route, Switch } from 'react-router-dom';

import http from 'api/http';
import { oilAPI } from 'api/requests';


import { ROUTE } from 'constants/routing';
import { ROLES } from 'constants/roles';
import { ACTION, STATUS } from 'constants/index';

/**
 * Scenes
 */
import NotFoundScene from 'Scenes/NotFoundScene';
import BrandsList from './List';
import BrandApplication from './Application';

import NotificationManager from 'dom-managers/notification';
import Button, { buttonColor, buttonType } from 'Components/Button';
import Modal from 'Components/Modal';

class OilBranderRouter extends React.Component {
  constructor(props) {
    props.updateUserRole(ROLES.OIL_BRANDER.serverName);

    super(props);
    this.state = {
      isModalOpen: false,
    };
  }

  roleSwitch = () => {
    const { user } = this.props;
    if (user.getActiveRole() !== ROLES.OIL_BRANDER.serverName) {
      user.changeMenu(ROLES.OIL_BRANDER.serverName);
    }
  };

  componentDidMount = () => {
    this.roleSwitch();
  };

  createActionsList = application => {
    const { user } = this.props;
    const currentYear = new Date().getFullYear();
    const isInternalUser = user.isInternal();
    const actions = [ACTION.DETAILS];

    if (
      (isInternalUser || application.applicationStatus !== STATUS.APPROVED) &&
      application.applicationStatus !== STATUS.DEACTIVATED
    ) {
      actions.push(ACTION.EDIT);
    }

    if (application.applicationStatus === STATUS.INCOMPLETE) {
      actions.push(ACTION.RESUBMIT);
    }

    if (isInternalUser && application.applicationStatus !== STATUS.DEACTIVATED) {
      actions.push(ACTION.DEACTIVATE);
    }

    if (isInternalUser && application.applicationStatus === STATUS.PENDING) {
      actions.push(ACTION.APPROVE);
    }

    if (isInternalUser && application.applicationStatus === STATUS.PENDING) {
      actions.push(ACTION.INCOMPLETE);
    }

    if (currentYear > application.year) {
      actions.push(ACTION.ROLL_OVER);
    }

    if (isInternalUser || (application.applicationStatus === STATUS.APPROVED && application.isPaid)) {
      actions.push(ACTION.E_SIGN_AGREEMENT);
    }

    return actions;
  };

  selectAction = (actionKey, application) => {
    const { user } = this.props;
    switch (actionKey) {
      case ACTION.DEACTIVATE:
        return new Promise((resolve, reject) => {
          this.modalBody = 'Are you sure you want to Deactivate this application?';
          this.modalConfirmAction = this._changeStatus(
            oilAPI.deactivateBrand(application.id),
            STATUS.DEACTIVATED,
            resolve
          );
          this._openModal();
        });
      case ACTION.INCOMPLETE:
        return new Promise((resolve, reject) => {
          this.modalBody = 'Are you sure you want to mark this application as Incomplete?';
          this.modalConfirmAction = this._changeStatus(
            oilAPI.incompleteBrand(application.id),
            STATUS.INCOMPLETE,
            resolve
          );
          this._openModal();
        });
      case ACTION.APPROVE:
        return new Promise((resolve, reject) => {
          this.modalBody = 'Are you sure you want to Approve this application?';
          this.modalConfirmAction = this._changeStatus(oilAPI.approveBrand(application.id), STATUS.APPROVED, resolve);
          this._openModal();
        });
      case ACTION.RESUBMIT:
        return new Promise((resolve, reject) => {
          this.modalBody = 'Are you sure you want to Resubmit this application?';
          this.modalConfirmAction = this._changeStatus(oilAPI.pendBrand(application.id), STATUS.PENDING, resolve);
          this._openModal();
        });
      case ACTION.ROLL_OVER:
        return new Promise((resolve, reject) => {
          this.modalBody = 'Are you sure you want to roll the brand over to the current year?';
          this.modalConfirmAction = this._rollOver(application.id, resolve);
          this.setState({ isModalOpen: true });
        });
      case ACTION.DETAILS:
        return new Promise((resolve, reject) => {
          user.goTo(
            `/${user.getManufacturerCode()}/${ROLES.OIL_BRANDER.nameForUrl}${ROLES.OIL_BRANDER.listSceneUrlPostfix}/${
              application.id
            }`
          );
        });
      case ACTION.EDIT:
        return new Promise((resolve, reject) => {
          user.goTo(
            `/${user.getManufacturerCode()}/${ROLES.OIL_BRANDER.nameForUrl}${ROLES.OIL_BRANDER.listSceneUrlPostfix}/${
              application.id
            }/edit`
          );
        });
      case ACTION.E_SIGN_AGREEMENT:
        return new Promise((resolve, reject) => {
          this.modalBody = 'Are you sure you want to generate and e-sign the Brand License Agreement?';
          this.modalConfirmAction = this.eSignAgreement(application.id, resolve);
          this.setState({ isModalOpen: true });
        });
      default:
        return () => {
          NotificationManager.error('note', 'No such action');
        };
    }
  };

  _changeStatus = (api, newStatusString, cb) => () => {
    this.setState({ requestInProcess: true }, () => {
      http
        .patch(api)
        .then(({ data }) => {
          cb(data || newStatusString);
          NotificationManager.success(1, 'The status was updated successfully.');
        })
        .finally(this._closeModal);
    });
  };

  _rollOver = (applicationId, cb) => () => {
    this.setState({ requestInProcess: true }, () => {
      http
        .post(oilAPI.rollOverBrand(applicationId))
        .then(({ data }) => {
          NotificationManager.success(1, 'Your brand has been successfully rolled over.');
          if (cb) cb(data);
        })
        .finally(this._closeModal);
    });
  };

  eSignAgreement = applicationId => () => {
    this.setState({ requestInProcess: true }, () => {
      http
        .get(oilAPI.eSignBrandLicense(applicationId))
        .then(() => {
          NotificationManager.success(
            1,
            'Brand License Agreement has been successfully generated and sent for e-signature to DocuSign.'
          );
        })
        .finally(this._closeModal);
    });
  };

  _openModal = () => {
    this.setState({ isModalOpen: true });
  };

  _closeModal = () => {
    this.setState({ isModalOpen: false, requestInProcess: false });
  };

  renderScene = Scene => {
    return ({ match }) => {
      return (
        <Scene
          user={this.props.user}
          createActionsList={this.createActionsList}
          selectAction={this.selectAction}
          match={match}
          manufacturerAddresses={this.props.manufacturerAddresses}
        />
      );
    };
  };

  render() {
    const { isModalOpen, requestInProcess } = this.state;
    const rootRoute = `${ROUTE.OIL_MANUFACTURER}${ROLES.OIL_BRANDER.listSceneUrlPostfix}`;

    return (
      <React.Fragment>
        <Switch>
          <Route path={`${rootRoute}/:applicationId`} render={this.renderScene(BrandApplication)} />
          <Route exact path={`${rootRoute}`} render={this.renderScene(BrandsList)} />
          <Route render={this.renderScene(NotFoundScene)} />
        </Switch>
        <Modal isOpen={isModalOpen}>
          <header className='ect-modal-header'>Confirmation</header>
          <div className='ect-modal-body'>{this.modalBody}</div>
          <footer className='ect-modal-footer'>
            <Button
              label='No'
              type={buttonType.secondary}
              color={buttonColor.gray}
              buttonClassName='_height_40 _width_160'
              onClick={this._closeModal}
            />
            <Button
              label='Yes'
              disabledWithSpinner={requestInProcess}
              buttonClassName='_height_40 _width_160'
              onClick={this.modalConfirmAction}
            />
          </footer>
        </Modal>
      </React.Fragment>
    );
  }
}

export default OilBranderRouter;



// WEBPACK FOOTER //
// ./src/app/Scenes/OilManufacturer/brand/index.jsx